import FormHeader from "@/components/FormHeader";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getListJalurJenis, getListUnitkerja } from "@/services/getData";
import { JalurJenis, Unitkerja } from "@/types";
import { ChevronRight } from "lucide-react";
import React, { useState } from "react";
import { LuListFilter } from "react-icons/lu";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";

export default function FormFilter({ closeModal }: { closeModal: () => void }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const idsUk = searchParams.get("ids_uk");
  const idsRefJalurJenis = searchParams.get("ids_ref_jalur_jenis") || "";
  const idsFakultas = searchParams.get("ids_fakultas") || ""; // Ambil param ids_fakultas

  const [selectedFilter, setSelectedFilter] = useState("JALUR");
  const [filter, setFilter] = useState<{
    program_studi: string[];
    jalur_jenis: string[];
    fakultas: string[];
  }>({
    program_studi: idsUk ? idsUk.split("-") : [],
    jalur_jenis: idsRefJalurJenis ? idsRefJalurJenis.split("-") : [],
    fakultas: idsFakultas ? idsFakultas.split("-") : [],
  });

  const [searchQueryProdi, setSearchQueryProdi] = useState("");
  const [searchQueryFakultas, setSearchQueryFakultas] = useState(""); // State search fakultas

  const { data: listUnitkerja, isPending: isPendingListUnitkerja } = useQuery({
    queryKey: ["listUnitkerja"],
    queryFn: getListUnitkerja,
  });

  const { data: listJalurJenis, isPending: isPendingListJalurJenis } = useQuery(
    {
      queryKey: ["listJalurJenis"],
      queryFn: getListJalurJenis,
    },
  );

  // Filter Prodi (5)
  const listProdi = listUnitkerja?.filter((unitkerja: Unitkerja) => {
    return unitkerja.unitkerja_group_key == "5" && unitkerja.is_active;
  });

  const filteredListProdi = listProdi
    ? listProdi.filter((prodi: Unitkerja) => {
        return prodi?.nama
          ?.toLowerCase()
          .includes(searchQueryProdi.toLowerCase());
      })
    : [];

  // Filter Fakultas (2)
  const listFakultas = listUnitkerja?.filter((unitkerja: Unitkerja) => {
    return unitkerja.unitkerja_group_key == "2" && unitkerja.is_active;
  });

  const filteredListFakultas = listFakultas
    ? listFakultas.filter((fakultas: Unitkerja) => {
        return fakultas?.nama
          ?.toLowerCase()
          .includes(searchQueryFakultas.toLowerCase());
      })
    : [];

  const handleApplyFilter = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (filter.jalur_jenis.length > 0) {
      searchParams.set("ids_ref_jalur_jenis", filter.jalur_jenis.join("-"));
    } else {
      searchParams.delete("ids_ref_jalur_jenis");
    }

    if (filter.program_studi.length > 0) {
      searchParams.set("ids_uk", filter.program_studi.join("-"));
    } else {
      searchParams.delete("ids_uk");
    }

    // Terapkan ke URL param ids_fakultas
    if (filter.fakultas.length > 0) {
      searchParams.set("ids_fakultas", filter.fakultas.join("-"));
    } else {
      searchParams.delete("ids_fakultas");
    }

    navigate(`/admin/dashboard?${searchParams.toString()}`);
    closeModal();
  };

  return (
    <form
      onSubmit={handleApplyFilter}
      className="pb-20 relative flex flex-col gap-y-3"
    >
      <FormHeader title="Filter Data Rekap" icon={<LuListFilter />} />

      {/* SECTION JALUR */}
      <section className="p-3 relative border rounded-xl">
        <div
          className="flex justify-between items-center cursor-pointer"
          onClick={() =>
            setSelectedFilter((prev) => (prev === "JALUR" ? "" : "JALUR"))
          }
        >
          <span className="font-bold">Jalur</span>
          <Button type="button" size={"icon"} variant={"outline"}>
            <ChevronRight
              className={`${
                selectedFilter === "JALUR" ? "rotate-90" : ""
              } duration-300`}
            />
          </Button>
        </div>
        {selectedFilter === "JALUR" &&
          (isPendingListJalurJenis ? (
            "Loading..."
          ) : (
            <div className="mt-3 grid grid-cols-3 gap-5">
              {listJalurJenis?.map((jalurJenis: JalurJenis) => {
                const isChecked = filter.jalur_jenis.includes(
                  String(jalurJenis.id_ref_jalur_jenis),
                );

                return (
                  <div
                    key={jalurJenis.id_ref_jalur_jenis}
                    className="flex gap-3"
                  >
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={(checked) => {
                        setFilter((prev) => {
                          const updatedJalurJenis = [...prev.jalur_jenis];

                          if (checked) {
                            if (
                              !updatedJalurJenis.includes(
                                String(jalurJenis.id_ref_jalur_jenis),
                              )
                            ) {
                              updatedJalurJenis.push(
                                String(jalurJenis.id_ref_jalur_jenis),
                              );
                            }
                          } else {
                            const index = updatedJalurJenis.indexOf(
                              String(jalurJenis.id_ref_jalur_jenis),
                            );
                            if (index > -1) {
                              updatedJalurJenis.splice(index, 1);
                            }
                          }

                          return {
                            ...prev,
                            jalur_jenis: updatedJalurJenis,
                          };
                        });
                      }}
                    />
                    <Label>{jalurJenis.nama_jalur_jenis}</Label>
                  </div>
                );
              })}
            </div>
          ))}
      </section>

      {/* SECTION FAKULTAS (BARU) */}
      <section className="p-3 relative border rounded-xl max-h-[50vh] overflow-auto">
        <div
          className="flex justify-between items-center cursor-pointer"
          onClick={() =>
            setSelectedFilter((prev) => (prev === "FAKULTAS" ? "" : "FAKULTAS"))
          }
        >
          <span className="font-bold">Fakultas</span>
          <Button type="button" size={"icon"} variant={"outline"}>
            <ChevronRight
              className={`${
                selectedFilter === "FAKULTAS" ? "rotate-90" : ""
              } duration-300`}
            />
          </Button>
        </div>
        {selectedFilter === "FAKULTAS" &&
          (isPendingListUnitkerja ? (
            "Loading..."
          ) : (
            <>
              <div className="py-3 sticky top-0 bg-background z-10">
                <Input
                  placeholder="Search Fakultas..."
                  value={searchQueryFakultas}
                  onChange={(event) =>
                    setSearchQueryFakultas(event.target.value)
                  }
                />
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-5">
                {filteredListFakultas?.map((fakultas: Unitkerja) => {
                  const isChecked = filter.fakultas.includes(
                    String(fakultas.uk_id),
                  );

                  return (
                    <div key={fakultas.uk_id} className="flex gap-3">
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={(checked) => {
                          setFilter((prev) => {
                            const updatedFakultas = [...prev.fakultas];

                            if (checked) {
                              if (
                                !updatedFakultas.includes(
                                  String(fakultas.uk_id),
                                )
                              ) {
                                updatedFakultas.push(String(fakultas.uk_id));
                              }
                            } else {
                              const index = updatedFakultas.indexOf(
                                String(fakultas.uk_id),
                              );
                              if (index > -1) {
                                updatedFakultas.splice(index, 1);
                              }
                            }

                            return {
                              ...prev,
                              fakultas: updatedFakultas,
                            };
                          });
                        }}
                      />
                      <Label>{fakultas.nama}</Label>
                    </div>
                  );
                })}
              </div>
            </>
          ))}
      </section>

      {/* SECTION PRODI */}
      <section className="p-3 relative border rounded-xl max-h-[50vh] overflow-auto">
        <div
          className="flex justify-between items-center cursor-pointer"
          onClick={() =>
            setSelectedFilter((prev) => (prev === "PRODI" ? "" : "PRODI"))
          }
        >
          <span className="font-bold">Program Studi</span>
          <Button type="button" size={"icon"} variant={"outline"}>
            <ChevronRight
              className={`${
                selectedFilter === "PRODI" ? "rotate-90" : ""
              } duration-300`}
            />
          </Button>
        </div>
        {selectedFilter === "PRODI" &&
          (isPendingListUnitkerja ? (
            "Loading..."
          ) : (
            <>
              <div className="py-3 sticky top-0 bg-background z-10">
                <Input
                  placeholder="Search Program Studi..."
                  value={searchQueryProdi}
                  onChange={(event) => setSearchQueryProdi(event.target.value)}
                />
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-5">
                {filteredListProdi?.map((prodi: Unitkerja) => {
                  const isChecked = filter.program_studi.includes(
                    String(prodi.uk_id),
                  );

                  return (
                    <div key={prodi.uk_id} className="flex gap-3">
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={(checked) => {
                          setFilter((prev) => {
                            const programStudi = [...prev.program_studi];

                            if (checked) {
                              if (!programStudi.includes(String(prodi.uk_id))) {
                                programStudi.push(String(prodi.uk_id));
                              }
                            } else {
                              const index = programStudi.indexOf(
                                String(prodi.uk_id),
                              );
                              if (index > -1) {
                                programStudi.splice(index, 1);
                              }
                            }

                            return {
                              ...prev,
                              program_studi: programStudi,
                            };
                          });
                        }}
                      />
                      <Label>{prodi.nama}</Label>
                    </div>
                  );
                })}
              </div>
            </>
          ))}
      </section>

      <div className="absolute bottom-3 left-3 right-3 bg-background z-20">
        <div className="flex justify-between">
          <div className="flex gap-3">
            <Button
              onClick={() => closeModal()}
              type="button"
              variant={"outline"}
            >
              Tutup
            </Button>
            <Button>Terapkan Filter</Button>
          </div>
          <Button
            type="button"
            variant={"outline"}
            onClick={() => {
              // Pastikan untuk mereset seluruh state filter termasuk fakultas
              setFilter((prev) => ({
                ...prev,
                program_studi: [],
                jalur_jenis: [],
                fakultas: [],
                // Jika sebelumnya ada state gelombang dsb, sesuaikan kembali di tipe/state-nya.
              }));
              closeModal();
              navigate("/admin/dashboard?menu=DATA");
            }}
          >
            Reset
          </Button>
        </div>
      </div>
    </form>
  );
}
